/**
 * CobrowseSession — Gestiona la conexión WebSocket del cliente con el hub
 * de co-browsing en eco_events (/ws/cobrowse).
 *
 * Ciclo de vida:
 *   idle → connecting → waiting (esperando agente) → active (agente unido) → ended
 *
 * El JWT se pasa como query-param (?token=...) porque la API nativa WebSocket
 * del navegador no permite cabeceras personalizadas en el upgrade.
 *
 * Reconexión: un cierre NO limpio del WebSocket (corte de wifi/datos, proxy,
 * reinicio del hub) NO termina la sesión de una — se reintenta con backoff
 * exponencial (hasta 6 intentos, 1s→8s) antes de recién ahí reportar error.
 * Mismo sessionId/token en cada intento, así que si el agente sigue
 * conectado del otro lado, la sesión se retoma transparente. Solo un cierre
 * LIMPIO (evt.wasClean, ej. el servidor cerró a propósito) o agotar los
 * reintentos terminan la sesión de verdad.
 */

export type CobrowseStatus = 'idle' | 'connecting' | 'waiting' | 'active' | 'ended' | 'error'

export interface CobrowseSessionOptions {
  /** URL base de eco_events, protocolo ws:// o wss://, sin barra al final. */
  wsBaseUrl: string
  /** JWT de sesión (emitido por el backend propio, verificable por eco_events). */
  token: string
  /** UUID generado en el cliente para identificar la sesión. */
  sessionId: string
  /** ID del usuario autenticado. */
  userId: string
  /** Ruta actual de la app (para contexto del agente). */
  path: string
  /** Detalle del caso que el usuario escribió al pedir soporte (HTML, puede
   * traer imágenes embebidas) — para contexto del agente, ver
   * SupportWidget.tsx. NO viaja en la URL del WS (podría superar un largo
   * seguro con imágenes embebidas): se manda como mensaje normal apenas la
   * conexión abre por primera vez (ver openSocket/sendSupportReason). En
   * una reconexión no se reenvía — el hub de eco_events ya lo tiene guardado
   * en la sesión desde el primer envío. */
  reason?: string
  onStatusChange?: (status: CobrowseStatus) => void
  onSessionCreated?: (sessionId: string) => void
  onSessionReady?: () => void
  onAgentControl?: (payload: unknown) => void
  onWebRTCSignal?: (payload: unknown) => void
  onSessionEnded?: () => void
  onError?: (message: string) => void
  /** El agente se desconectó transitoriamente (reload, wifi) — la sesión
   * sigue activa del lado de eco_events, solo informativo. */
  onAgentDisconnected?: () => void
  /** El agente volvió a tiempo tras onAgentDisconnected. */
  onAgentReconnected?: () => void
  /** El agente pidió recargar esta página. La sesión no se pierde: el
   * sessionId ya está persistido en sessionStorage y el hook reconecta solo
   * al volver a montar (ver useCobrowseSupport). */
  onReloadPage?: () => void
}

export class CobrowseSession {
  private ws: WebSocket | null = null
  private readonly opts: CobrowseSessionOptions
  public readonly sessionId: string
  private _status: CobrowseStatus = 'idle'

  // Reconexión automática ante cortes de red transitorios (estilo Meet: un
  // corte de wifi/datos de unos segundos no debe terminar la sesión de
  // soporte). Solo aplica a cierres NO limpios del WebSocket (evt.wasClean
  // = false) — nunca a end()/destroy() explícitos (ver manualClose) ni a un
  // 'session_ended' real que mande el servidor.
  private reconnectAttempts = 0
  private readonly maxReconnectAttempts = 6
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private manualClose = false
  // true = ya se mandó opts.reason (una sola vez, en la conexión inicial —
  // ver openSocket). No se reenvía en reconexiones, eco_events ya lo tiene.
  private reasonSent = false

  constructor(opts: CobrowseSessionOptions) {
    this.opts = opts
    this.sessionId = opts.sessionId
  }

  get status(): CobrowseStatus {
    return this._status
  }

  private setStatus(s: CobrowseStatus): void {
    this._status = s
    this.opts.onStatusChange?.(s)
  }

  /** Abre la conexión WebSocket hacia eco_events (primera vez). */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return
    this.manualClose = false
    this.reconnectAttempts = 0
    this.setStatus('connecting')
    this.openSocket()
  }

  /** Crea el WebSocket — usado tanto por connect() como por cada reintento de reconexión. */
  private openSocket(): void {
    const { wsBaseUrl, token, sessionId, userId, path } = this.opts
    const base = wsBaseUrl.replace(/\/$/, '')
    const url = [
      `${base}/ws/cobrowse`,
      `?token=${encodeURIComponent(token)}`,
      `&role=client`,
      `&sessionId=${encodeURIComponent(sessionId)}`,
      `&userId=${encodeURIComponent(userId)}`,
      `&path=${encodeURIComponent(path)}`,
    ].join('')

    const ws = new WebSocket(url)
    this.ws = ws

    ws.onopen = () => {
      // Reconexión exitosa (o primera conexión) — resetea el contador para
      // que un futuro corte tenga sus reintentos completos otra vez.
      this.reconnectAttempts = 0
      // El hub responde con session_created; hasta entonces permanecemos en
      // waiting — salvo que ya estuviéramos 'active' (reconexión a mitad de
      // sesión ya iniciada): no retrocedemos el estado visible por un corte
      // breve, el agente ya estaba unido.
      if (this._status !== 'active') this.setStatus('waiting')

      // Detalle del caso — se manda una sola vez, en la conexión inicial
      // (el server ya registró el rol=client antes de que este evento
      // dispare, así que el mensaje llega en orden sin condición de carrera).
      if (this.opts.reason && !this.reasonSent) {
        this.reasonSent = true
        this.sendSupportReason(this.opts.reason)
      }
    }

    ws.onmessage = (evt: MessageEvent<string>) => {
      try {
        const msg = JSON.parse(evt.data) as {
          type: string
          sessionId?: string
          payload?: unknown
          message?: string
        }
        this.dispatch(msg)
      } catch {
        // frame malformado — ignorar
      }
    }

    ws.onclose = (evt) => {
      if (this.manualClose || this._status === 'ended') return
      if (evt.wasClean) {
        this.setStatus('ended')
        this.opts.onSessionEnded?.()
        return
      }
      // Cierre NO limpio (corte de red, proxy, reinicio del hub, etc.) —
      // reintentar antes de dar la sesión por perdida, en vez de terminarla
      // al primer corte (ver scheduleReconnect).
      this.scheduleReconnect()
    }

    ws.onerror = () => {
      // onerror va seguido de onclose para el mismo evento a nivel browser
      // — dejamos que onclose decida entre reintentar o terminar, acá no
      // hacemos nada para no duplicar el manejo ni disparar onError dos veces.
    }
  }

  /** Reintenta la conexión con backoff exponencial (1s, 2s, 4s, 8s, 8s, 8s) hasta agotar los intentos. */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.setStatus('error')
      this.opts.onError?.('Conexión de soporte interrumpida')
      return
    }
    this.reconnectAttempts++
    const delay = Math.min(1000 * 2 ** (this.reconnectAttempts - 1), 8000)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.openSocket()
    }, delay)
  }

  private dispatch(msg: {
    type: string
    sessionId?: string
    payload?: unknown
    message?: string
  }): void {
    switch (msg.type) {
      case 'session_created':
        // Mismo cuidado que en onopen: no retroceder desde 'active' si el
        // servidor reenvía session_created en una reconexión a mitad de sesión.
        if (this._status !== 'active') this.setStatus('waiting')
        this.opts.onSessionCreated?.(msg.sessionId ?? this.sessionId)
        break
      case 'session_ready':
        this.setStatus('active')
        this.opts.onSessionReady?.()
        break
      case 'agent_control':
        this.opts.onAgentControl?.(msg.payload)
        break
      case 'webrtc_signal':
        this.opts.onWebRTCSignal?.(msg.payload)
        break
      case 'session_ended':
        this.setStatus('ended')
        this.opts.onSessionEnded?.()
        break
      case 'agent_disconnected':
        this.opts.onAgentDisconnected?.()
        break
      case 'agent_reconnected':
        this.opts.onAgentReconnected?.()
        break
      case 'reload_page':
        this.opts.onReloadPage?.()
        break
      case 'pong':
        // keepalive del servidor — no hacer nada
        break
      case 'error':
        this.setStatus('error')
        this.opts.onError?.(msg.message ?? 'Error de sesión de soporte')
        break
      default:
        // tipo desconocido — ignorar
        break
    }
  }

  /**
   * Envía un evento rrweb al hub para que lo reenvíe al agente.
   * No-op si el WebSocket no está abierto.
   */
  sendRRWebEvent(event: unknown): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    try {
      this.ws.send(
        JSON.stringify({
          type: 'rrweb_event',
          sessionId: this.sessionId,
          payload: event,
        }),
      )
    } catch {
      // send falló (buffer lleno, WS cerrándose) — descartar frame
    }
  }

  /** Envía un payload de señalización WebRTC (SDP/ICE) al otro extremo. */
  sendWebRTCSignal(payload: unknown): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    try {
      this.ws.send(
        JSON.stringify({
          type: 'webrtc_signal',
          sessionId: this.sessionId,
          payload,
        }),
      )
    } catch {
      // ignore
    }
  }

  /** Envía (o actualiza) el detalle del caso — ver comentario en
   * CobrowseSessionOptions.reason sobre por qué no viaja en la URL. */
  sendSupportReason(reason: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    try {
      this.ws.send(
        JSON.stringify({
          type: 'support_reason',
          sessionId: this.sessionId,
          payload: { reason },
        }),
      )
    } catch {
      // ignore
    }
  }

  /** Informa al agente si el control remoto está permitido ahora mismo. */
  sendControlState(enabled: boolean): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    try {
      this.ws.send(
        JSON.stringify({
          type: 'control_state',
          sessionId: this.sessionId,
          payload: { enabled },
        }),
      )
    } catch {
      // ignore
    }
  }

  /**
   * Reporta la posición/tamaño de la ventana del navegador — necesario del
   * lado del agente para traducir un clic sobre el video de pantalla
   * completa a coordenadas relativas al viewport real de esta página (ver
   * screen-share.ts).
   */
  sendScreenGeometry(geometry: {
    screenX: number
    screenY: number
    outerWidth: number
    outerHeight: number
    innerWidth: number
    innerHeight: number
    devicePixelRatio: number
  }): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    try {
      this.ws.send(
        JSON.stringify({
          type: 'screen_geometry',
          sessionId: this.sessionId,
          payload: geometry,
        }),
      )
    } catch {
      // ignore
    }
  }

  /** Termina la sesión limpiamente, notificando al hub. */
  end(): void {
    this.manualClose = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.setStatus('ended')
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: 'end_session', sessionId: this.sessionId }))
      } catch {
        // ignore
      }
    }
    this.ws?.close(1000, 'user ended session')
    this.ws = null
  }

  /** Cierra el WebSocket sin notificar al hub (cleanup de emergencia). */
  destroy(): void {
    this.manualClose = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.ws?.close()
    this.ws = null
  }
}
